import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { doc, onSnapshot, updateDoc, collection, query, where, onSnapshot as onSnapshotQuery } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getExperienceType } from '../data/experienceTypes';
import { getExperienceTypeV2 } from '../data/experienceTypesV2';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import ChatWidget from '../components/chat/ChatWidget';
import Icon from '../components/shared/Icon';
import ShareTipModal from '../components/community/ShareTipModal';
import ExplorePhase from '../components/experiences/ExplorePhase';
import DiscussPhase from '../components/experiences/DiscussPhase';
import ExecutePhase from '../components/experiences/ExecutePhase';
import DebriefPhase from '../components/experiences/DebriefPhase';
import ZoomableTimeline from '../components/experiences/ZoomableTimeline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { PageHeader, Button } from '../design-system';

// Support both old and new phase names for backward compatibility
const PHASES = ['exposure', 'discussion', 'execution', 'explore', 'discuss', 'execute', 'debrief'];
const PHASE_LABELS = ['Explore', 'Decide', 'Execute', 'Debrief'];
const PHASE_MAP = {
  'exposure': 'explore',
  'discussion': 'discuss',
  'execution': 'execute',
  'explore': 'explore',
  'discuss': 'discuss',
  'execute': 'execute',
  'debrief': 'debrief'
};

export default function ExperiencePlanning() {
  const { experienceId } = useParams();
  const navigate = useNavigate();
  const [experience, setExperience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhase, setCurrentPhase] = useState('exposure');
  const [showChat, setShowChat] = useState(false);
  const [showShareTipModal, setShowShareTipModal] = useState(false);
  const { user } = useAuth();
  const { family } = useFamily();

  useEffect(() => {
    if (!experienceId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'projects', experienceId),
      (doc) => {
        if (doc.exists()) {
          const data = { id: doc.id, ...doc.data() };
          setExperience(data);
          // Set current phase from experience or default to exposure/explore
          if (data.currentPhase && PHASES.includes(data.currentPhase)) {
            // Normalize phase name (map old to new)
            const normalizedPhase = PHASE_MAP[data.currentPhase] || data.currentPhase;
            setCurrentPhase(normalizedPhase);
          }
        }
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [experienceId]);

  const handlePhaseChange = async (phase) => {
    // Normalize phase name
    const normalizedPhase = PHASE_MAP[phase] || phase;
    setCurrentPhase(normalizedPhase);
    // Update experience phase in Firestore
    if (experienceId) {
      try {
        await updateDoc(doc(db, 'projects', experienceId), {
          currentPhase: normalizedPhase,
        });
      } catch (error) {
        console.error('Error updating phase:', error);
      }
    }
  };

  const handleReaction = async (category, optionId, reaction) => {
    if (!experienceId) return;
    
    try {
      // Use dot notation for nested updates
      await updateDoc(doc(db, 'projects', experienceId), {
        [`exposure.categories.${category}.preferences.${optionId}.reaction`]: reaction,
      });
    } catch (error) {
      console.error('Error updating reaction:', error);
    }
  };

  const handleComment = async (category, optionId, comment) => {
    if (!experienceId) return;
    
    try {
      // Use dot notation for nested updates
      await updateDoc(doc(db, 'projects', experienceId), {
        [`exposure.categories.${category}.preferences.${optionId}.comment`]: comment,
      });
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4 animate-bounce"><Icon name="🏡" size="4x" className="text-primary" /></div>
          <div className="text-lg text-text-secondary">Loading experience...</div>
        </div>
      </div>
    );
  }

  if (!experience) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4"><Icon name="❌" size="4x" className="text-text-secondary" /></div>
          <div className="text-lg text-text-secondary mb-4">Experience not found</div>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-white rounded-xl hover:shadow-lg transition-all touch-target active:scale-95"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const getPhaseStatus = (phaseIndex) => {
    // Map current phase to index (support both old and new names)
    const phaseToIndex = {
      'exposure': 0, 'explore': 0,
      'discussion': 1, 'discuss': 1,
      'execution': 2, 'execute': 2,
      'debrief': 3
    };
    const currentIndex = phaseToIndex[currentPhase] ?? 0;
    if (phaseIndex < currentIndex) return 'completed';
    if (phaseIndex === currentIndex) return 'active';
    return '';
  };

  const getOptionData = (category, optionId) => {
    if (!experience?.exposure?.categories?.[category]?.preferences?.[optionId]) {
      return { reaction: '', comment: '' };
    }
    const pref = experience.exposure.categories[category].preferences[optionId];
    return {
      reaction: pref.reaction || '',
      comment: pref.comment || '',
    };
  };

  const subtitleContent = (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="text-xs sm:text-sm text-text-secondary truncate">
        {experience.guests ? `${experience.guests} guests` : '12 guests'} • {experience.date ? (experience.date.toDate ? experience.date.toDate().toLocaleDateString() : new Date(experience.date).toLocaleDateString()) : 'April 12, 6pm'}
      </div>
      {family?.members && family.members.length > 1 && (
        <div className="flex items-center gap-1.5 text-xs text-text-secondary">
          <span>•</span>
          <span>Collaborating with:</span>
          <div className="flex items-center gap-1">
            {family.members
              .filter((memberId) => memberId !== user?.uid)
              .map((memberId) => (
                <div
                  key={memberId}
                  className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-semibold"
                  title={family.onboarding?.partnerName || 'Partner'}
                >
                  {family.onboarding?.partnerName?.charAt(0) || 'P'}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white pt-14 pb-20 md:pt-0 md:pb-0">
      <PageHeader
        title={experience.title || 'Passover Seder 2025'}
        subtitle={subtitleContent}
        onBack={() => navigate('/')}
        actions={
          <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant="secondary"
              onClick={() => setShowChat(true)}
              className="flex-1 sm:flex-none min-h-[44px]"
            >
              <Icon name="💬" className="mr-1" /> <span className="hidden sm:inline">Chat Helper</span>
            </Button>
            <Button className="flex-1 sm:flex-none min-h-[44px]">
              <span className="hidden sm:inline">Save </span>Progress
            </Button>
          </div>
        }
      />

      {/* Phase Navigation - Mobile optimized with horizontal scroll */}
      <div className="bg-white border-b border-border overflow-x-auto no-scrollbar">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 flex gap-4 sm:gap-6 md:gap-8 min-w-max sm:min-w-0">
          {PHASE_LABELS.map((label, index) => {
            const phase = PHASES[index];
            const status = getPhaseStatus(index);
            return (
              <button
                key={phase}
                onClick={() => handlePhaseChange(phase)}
                className={`py-3 sm:py-4 text-xs sm:text-sm font-semibold border-b-[3px] transition-all whitespace-nowrap touch-target min-h-[44px] ${
                  status === 'active'
                    ? 'text-primary border-primary'
                    : 'text-text-secondary border-transparent hover:text-primary active:bg-gray-50'
                }`}
              >
                <span
                  className={`inline-block w-5 h-5 sm:w-6 sm:h-6 rounded-full text-center text-[10px] sm:text-xs leading-5 sm:leading-6 mr-1.5 sm:mr-2 ${
                    status === 'active'
                      ? 'bg-primary text-white'
                      : status === 'completed'
                      ? 'bg-primary-light text-white'
                      : 'bg-[#F0F0F0] text-text-secondary'
                  }`}
                >
                  {status === 'completed' ? '✓' : index + 1}
                </span>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content - Mobile optimized spacing */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-4 sm:py-6 md:py-8 pb-16 sm:pb-20 md:pb-24">
        {/* Phase 1: Explore (V2) */}
        {(currentPhase === 'explore' || currentPhase === 'exposure') && experience && (
          <>
            {(experience.decisionCards || experience.typeId || getExperienceTypeV2(experience.type || experience.typeId)) ? (
              <ExplorePhase experienceId={experienceId} experience={experience} />
            ) : (
              /* Fallback to old exposure phase */
              <div className="animate-fadeIn">
            <div className="mb-6 sm:mb-8">
              <h1 className="font-sans text-xl sm:text-2xl md:text-3xl font-semibold mb-2 sm:mb-3 tracking-tight">Explore What's Possible</h1>
              <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
                Browse options and mark what resonates. This helps you (and your partner) discover your vision.
              </p>
            </div>

            {/* Dynamic Categories based on Experience Type */}
            {(() => {
              const experienceType = getExperienceType(experience.type || 'passover-seder');
              return experienceType.exposureCategories?.map((category) => (
                <div key={category.id} className="mb-8 sm:mb-10 md:mb-12">
                  <div className="flex justify-between items-center mb-4 sm:mb-5">
                    <h3 className="text-lg sm:text-xl font-semibold text-text-primary">{category.name}</h3>
                    {category.options && category.options.length > 3 && (
                      <div className="flex gap-2">
                        <button className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-all cursor-pointer text-sm sm:text-base">
                          ←
                        </button>
                        <button className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-all cursor-pointer text-sm sm:text-base">
                          →
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4 sm:gap-5 mb-4">
                    {category.options?.map((option) => {
                      const data = getOptionData(category.id, option.id);
                      return (
                        <OptionCard
                          key={option.id}
                          id={option.id}
                          category={category.id}
                          icon={option.icon}
                          title={option.title}
                          description={option.description}
                          reaction={data.reaction}
                          comment={data.comment}
                          onReaction={(reaction) => handleReaction(category.id, option.id, reaction)}
                          onComment={(comment) => handleComment(category.id, option.id, comment)}
                        />
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
              </div>
            )}
          </>
        )}

        {/* Phase 2: Discuss (V2) */}
        {(currentPhase === 'discuss' || currentPhase === 'discussion') && experience && (
          <>
            {(experience.decisionCards || experience.typeId || getExperienceTypeV2(experience.type || experience.typeId)) ? (
              <DiscussPhase experienceId={experienceId} experience={experience} />
            ) : (
              /* Fallback to old discussion phase */
              <div className="animate-fadeIn">
            <div className="mb-6 sm:mb-8">
              <h1 className="font-sans text-xl sm:text-2xl md:text-3xl font-semibold mb-2 sm:mb-3 tracking-tight">Make Your Decisions</h1>
              <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
                Work through the details with your partner. Lock in what's decided, discuss what needs more thought.
              </p>
            </div>

            <div className="flex flex-col gap-5">
              {/* Basics Decision - Always show */}
              <DecisionCard
                decisionKey="basics"
                title="Basics"
                status={experience.decisions?.basics?.status || 'research'}
                content={
                  <>
                    <strong>Date:</strong> {experience.date ? (experience.date.toDate ? experience.date.toDate().toLocaleDateString() : new Date(experience.date).toLocaleDateString()) : 'Not set'}<br />
                    <strong>Guests:</strong> {experience.guests || 0} {experience.guests === 1 ? 'person' : 'people'}<br />
                    <strong>Location:</strong> {experience.location || 'Not set'}
                  </>
                }
                experience={experience}
                experienceId={experienceId}
              />

              {/* Dynamic Decisions based on Experience Type */}
              {(() => {
                const experienceType = getExperienceType(experience.type || 'passover-seder');
                return experienceType.defaultDecisions
                  ?.filter((d) => d.key !== 'basics')
                  .map((decision) => {
                    const decisionData = experience.decisions?.[decision.key] || { status: decision.status, value: '', thread: [] };
                    return (
                      <DecisionCard
                        key={decision.key}
                        decisionKey={decision.key}
                        title={decision.title}
                        status={decisionData.status}
                        content={decisionData.value || `Decide on ${decision.title.toLowerCase()}`}
                        experience={experience}
                        experienceId={experienceId}
                        hasThread={decisionData.status === 'discussing'}
                        family={family}
                        currentUserId={user?.uid}
                      />
                    );
                  });
              })()}
            </div>
              </div>
            )}
          </>
        )}

        {/* Phase 3: Execute (V2) */}
        {(currentPhase === 'execute' || currentPhase === 'execution') && experience && (
          <>
            {experience.tasks !== undefined ? (
              <ExecutePhase experienceId={experienceId} experience={experience} />
            ) : (
              /* Fallback to old execution phase */
              <div className="animate-fadeIn">
            <div className="mb-6 sm:mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2 sm:mb-3">
                <div>
                  <h1 className="font-sans text-xl sm:text-2xl md:text-3xl font-semibold mb-2 sm:mb-3 tracking-tight">Bring It to Life</h1>
                  <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
                    Your vision is set. Here's your plan to make it happen.
                  </p>
                </div>
                <button
                  onClick={() => setShowShareTipModal(true)}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors text-sm sm:text-base whitespace-nowrap"
                >
                  <Icon name="✨" className="mr-2 inline" /> Share Your Hack
                </button>
              </div>
            </div>

            {/* Budget Widget */}
            <BudgetWidget experience={experience} experienceId={experienceId} />

            {/* Timeline - Use ZoomableTimeline for V2 experiences */}
            {experience.timeline !== undefined || experience.decisionCards ? (
              <ZoomableTimeline experience={experience} experienceId={experienceId} />
            ) : (
              <Timeline experience={experience} experienceId={experienceId} />
            )}
              </div>
            )}
          </>
        )}

        {/* Phase 4: Debrief */}
        {currentPhase === 'debrief' && experience && (
          <DebriefPhase experienceId={experienceId} experience={experience} />
        )}
      </div>

      {/* Chat Widget - Context-aware for this experience */}
      <ChatWidget currentExperience={experience} isOpen={showChat} onClose={setShowChat} />

      {/* Share Tip Modal */}
      <ShareTipModal
        isOpen={showShareTipModal}
        onClose={() => setShowShareTipModal(false)}
        projectType={experience?.type}
        projectTitle={experience?.title}
      />
    </div>
  );
}

// Decision Card Component
function DecisionCard({ decisionKey, title, status, content, experience, experienceId, hasThread, actionButton, hasOptions, options, family, currentUserId }) {
  const [newComment, setNewComment] = useState('');
  const decision = experience?.decisions?.[decisionKey] || {};

  const getMemberName = (userId) => {
    if (!userId || !family?.members) return 'Unknown';
    if (userId === currentUserId) return 'You';
    // Try to get name from family data or user display name
    // For now, return a generic name
    const memberIndex = family.members.indexOf(userId);
    return family.onboarding?.partnerName || `Family Member ${memberIndex + 1}`;
  };

  const getMemberInitial = (userId) => {
    if (!userId) return '?';
    if (userId === currentUserId) return 'Y';
    const name = getMemberName(userId);
    return name.charAt(0).toUpperCase();
  };

  const handleStatusChange = async (newStatus) => {
    if (!experienceId || !currentUserId) return;
    try {
      await updateDoc(doc(db, 'projects', experienceId), {
        [`decisions.${decisionKey}.status`]: newStatus,
        [`decisions.${decisionKey}.lockedBy`]: newStatus === 'locked' ? currentUserId : null,
        [`decisions.${decisionKey}.lockedAt`]: newStatus === 'locked' ? new Date() : null,
      });
    } catch (error) {
      console.error('Error updating decision status:', error);
    }
  };

  const handleAddComment = async () => {
    if (!experienceId || !newComment.trim() || !currentUserId) return;
    try {
      const thread = decision.thread || [];
      const updatedThread = [
        ...thread,
        {
          author: getMemberName(currentUserId),
          authorId: currentUserId,
          text: newComment,
          timestamp: new Date(),
        },
      ];
      await updateDoc(doc(db, 'projects', experienceId), {
        [`decisions.${decisionKey}.thread`]: updatedThread,
      });
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  return (
    <div
      className={`bg-white rounded-card p-4 sm:p-6 border shadow-card ${
        status === 'locked'
          ? 'border-primary-light bg-surface'
          : status === 'discussing'
          ? 'border-accent'
          : 'border-border'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0 mb-3 sm:mb-4">
        <div className="flex items-center gap-2">
          <div className="text-base sm:text-lg font-semibold text-text-primary">{title}</div>
          {status === 'locked' && decision.lockedBy && (
            <span className="text-xs text-text-secondary">
              by {decision.lockedBy === currentUserId ? 'you' : getMemberName(decision.lockedBy)}
            </span>
          )}
        </div>
        <div
          className={`px-3 py-1 rounded-xl text-[11px] font-semibold uppercase tracking-wide ${
            status === 'locked'
              ? 'bg-primary-light text-white'
              : status === 'discussing'
              ? 'bg-accent text-white'
              : 'bg-[#F0F0F0] text-text-secondary'
          }`}
        >
          {status === 'locked' && '✓ Decided'}
          {status === 'discussing' && <><Icon name="💬" className="mr-1" /> Discussing</>}
          {status === 'research' && <><Icon name="🔍" className="mr-1" /> Need Input</>}
        </div>
      </div>
      <div className="text-sm sm:text-[15px] text-text-primary leading-relaxed mb-3 sm:mb-4">{content}</div>

      {hasThread && (
        <div className="bg-surface rounded-xl p-4 mt-3">
          {decision.thread && decision.thread.length > 0 && (
            <>
              {decision.thread.map((message, index) => (
                <div key={index} className="flex gap-3 mb-3 last:mb-0">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    {message.authorId ? getMemberInitial(message.authorId) : (message.author === 'You' ? 'Y' : message.author?.charAt(0) || '?')}
                  </div>
                  <div className="flex-1">
                    <div className="text-[13px] font-semibold text-text-primary mb-1">
                      {message.author || getMemberName(message.authorId)}
                      {message.authorId === currentUserId && (
                        <span className="text-xs text-text-secondary ml-2">(you)</span>
                      )}
                    </div>
                    <div className="text-sm text-text-secondary leading-relaxed">{message.text}</div>
                  </div>
                </div>
              ))}
            </>
          )}
                    <div className="flex flex-col sm:flex-row gap-2 mt-3">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        className="flex-1 px-3 py-2 sm:py-2.5 border border-border rounded-lg text-xs sm:text-sm font-sans resize-y min-h-[60px] focus:outline-none focus:border-primary transition-all"
                        placeholder="Add your thoughts..."
                      />
                      <button
                        onClick={handleAddComment}
                        className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover"
                      >
                        Reply
                      </button>
                    </div>
        </div>
      )}

      {actionButton && (
        <button
          onClick={() => {}}
          className="mt-3 w-full sm:w-auto px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold bg-white border border-border text-text-secondary hover:border-primary hover:text-primary transition-all"
        >
          {actionButton}
        </button>
      )}

      {hasOptions && options && (
        <div className="mt-3 flex flex-col gap-2">
          {options.map((option, index) => (
            <div key={index} className="px-3 py-2.5 sm:py-3 bg-surface rounded-lg text-xs sm:text-sm">
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Budget Widget Component
function BudgetWidget({ experience, experienceId }) {
  const budget = experience?.budget || { allocated: 600, spent: 0 };
  const spent = budget.spent || 0;
  const allocated = budget.allocated || 600;
  const percentage = allocated > 0 ? Math.min((spent / allocated) * 100, 100) : 0;

  return (
    <div className="bg-primary rounded-card p-4 sm:p-6 text-white mb-6 sm:mb-8">
      <div className="text-xs sm:text-sm font-semibold opacity-90 mb-3 sm:mb-4">Your Budget</div>
      <div className="flex justify-between mb-2 sm:mb-3">
        <div>
          <div className="text-[11px] sm:text-[13px] opacity-90">Spent</div>
          <div className="text-xl sm:text-2xl font-semibold">${spent}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] sm:text-[13px] opacity-90">Budget</div>
          <div className="text-xl sm:text-2xl font-semibold">${allocated}</div>
        </div>
      </div>
      <div className="h-2 bg-white/20 rounded overflow-hidden">
        <div
          className="h-full bg-white transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// Timeline Component
function Timeline({ experience, experienceId }) {
  const timeline = experience?.timeline || [];
  
  // Get default timeline from experience type if none exists
  const experienceType = getExperienceType(experience?.type || 'passover-seder');
  const defaultTimeline = experienceType.defaultTimeline || [];

  const displayTimeline = timeline.length > 0 ? timeline : defaultTimeline;

  const handleTaskToggle = async (milestoneId, taskId, completed) => {
    if (!experienceId) return;
    try {
      const updatedTimeline = displayTimeline.map((milestone) => {
        if (milestone.id === milestoneId) {
          return {
            ...milestone,
            tasks: milestone.tasks.map((task) =>
              task.id === taskId ? { ...task, completed: !completed } : task
            ),
          };
        }
        return milestone;
      });
      await updateDoc(doc(db, 'projects', experienceId), {
        timeline: updatedTimeline,
      });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  return (
    <div className="relative">
      {displayTimeline.map((milestone, index) => (
        <div key={milestone.id || index} className="flex gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="w-6 sm:w-10 flex-shrink-0 flex flex-col items-center">
            <div
              className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full border sm:border-[3px] border-white ${
                milestone.status === 'complete'
                  ? 'bg-primary-light shadow-[0_0_0_2px_#FF5A7F]'
                  : milestone.status === 'active'
                  ? 'bg-primary shadow-[0_0_0_2px_#FF385C]'
                  : 'bg-border shadow-[0_0_0_2px_#DDDDDD]'
              }`}
            />
            {index < displayTimeline.length - 1 && (
              <div className="w-0.5 flex-1 bg-border my-1" />
            )}
          </div>
          <div className="flex-1 bg-white rounded-card p-4 sm:p-6 border border-border">
            <div className="text-[10px] sm:text-xs font-semibold text-primary uppercase tracking-wide mb-2 sm:mb-3">
              {milestone.date}
            </div>
            <div className="flex flex-col gap-3">
              {milestone.tasks?.map((task) => (
                <div
                  key={task.id}
                  className={`flex items-start gap-3 p-3 bg-surface rounded-lg cursor-pointer hover:bg-[#F0F0F0] transition-colors ${
                    task.completed ? 'opacity-60' : ''
                  }`}
                  onClick={() => handleTaskToggle(milestone.id || index, task.id, task.completed)}
                >
                  <div
                    className={`w-5 h-5 border rounded-md flex-shrink-0 mt-0.5 flex items-center justify-center ${
                      task.completed
                        ? 'bg-primary-light border-primary-light'
                        : 'border-border'
                    }`}
                  >
                    {task.completed && (
                      <span className="text-white text-xs font-semibold">✓</span>
                    )}
                  </div>
                  <div className="flex-1 text-sm text-text-primary font-medium">{task.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Option Card Component
function OptionCard({ id, category, icon, title, description, reaction, comment, onReaction, onComment }) {
  const [localComment, setLocalComment] = useState(comment || '');
  const [showCommentInput, setShowCommentInput] = useState(!!comment);

  const handleReactionClick = (reactionType) => {
    onReaction(reactionType === reaction ? '' : reactionType);
  };

  const handleCommentSubmit = () => {
    onComment(localComment);
    if (!localComment) {
      setShowCommentInput(false);
    }
  };

  return (
    <div
      className={`bg-white rounded-card overflow-hidden border border-border cursor-pointer transition-all shadow-card hover:shadow-card-hover ${
        reaction === 'love' ? 'border-primary-light' : reaction === 'pass' ? 'opacity-50' : 'border-border'
      }`}
    >
      <div className="w-full h-[140px] sm:h-[180px] bg-surface flex items-center justify-center rounded-t-card">
        <Icon name={icon} size="4x" className="text-primary" />
      </div>
      <div className="p-4 sm:p-5">
        <div className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">{title}</div>
        <div className="text-xs sm:text-sm text-text-secondary leading-relaxed mb-3 sm:mb-4">{description}</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => handleReactionClick('love')}
            className={`flex-1 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold border transition-all hover:-translate-y-0.5 ${
              reaction === 'love'
                ? 'border-primary-light bg-primary-light text-white'
                : 'border-border bg-white'
            }`}
          >
            <Icon name="❤️" className="mr-1" /> Love It
          </button>
          <button
            onClick={() => handleReactionClick('maybe')}
            className={`flex-1 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold border transition-all hover:-translate-y-0.5 ${
              reaction === 'maybe'
                ? 'border-primary-light bg-primary-light text-white'
                : 'border-border bg-white'
            }`}
          >
            👍 Maybe
          </button>
          <button
            onClick={() => handleReactionClick('pass')}
            className={`flex-1 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold border transition-all hover:-translate-y-0.5 ${
              reaction === 'pass'
                ? 'border-red-500 bg-red-500 text-white'
                : 'border-border bg-white'
            }`}
          >
            👎 Pass
          </button>
        </div>
        {showCommentInput || reaction ? (
            <input
              type="text"
              value={localComment}
              onChange={(e) => setLocalComment(e.target.value)}
              onBlur={handleCommentSubmit}
              onKeyPress={(e) => e.key === 'Enter' && handleCommentSubmit()}
              className="w-full mt-2 px-3 py-2 sm:py-2.5 border border-border rounded-lg text-base font-sans focus:outline-none focus:border-primary transition-all"
              placeholder="Add a note..."
            />
          ) : (
            <button
              onClick={() => setShowCommentInput(true)}
              className="w-full mt-2 text-left text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors"
            >
              + Add a note
            </button>
          )}
      </div>
    </div>
  );
}
